import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, CheckCircle2, ArrowLeft } from "lucide-react";
import { useLang } from "../i18n";
import api, { formatApiError } from "../api";

const EMPTY = {
  kind: "fattura", name: "", address_street: "", address_postal_code: "",
  address_city: "", address_province: "", country: "Italia",
  vat_number: "", tax_code: "", sdi_code: "", pec: "",
};

const InvoiceRequest = () => {
  const { t } = useLang();
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invoice-requests/${token}`)
      .then(({ data }) => { setReq(data); if (data.status === "issued") setDone(true); })
      .catch(() => setNotFound(true));
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = { ...form };
      ["vat_number", "tax_code", "sdi_code", "pec", "address_province"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      await api.post(`/invoice-requests/${token}`, payload);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none transition-colors";
  const label = "block font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute mb-2";

  return (
    <main className="pt-16 min-h-screen px-5 py-14" data-testid="invoice-request-page">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute hover:text-ember transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("story_back")}
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <FileText className="w-6 h-6 text-ember" />
          <h1 className="font-display font-bold text-2xl" data-testid="invoice-title">{t("inv_title")}</h1>
        </div>

        {notFound && <p className="text-red-400 text-sm" data-testid="invoice-not-found">{t("inv_not_found")}</p>}

        {req && !notFound && (
          <>
            <p className="text-mute text-sm mb-8" data-testid="invoice-sub">
              {t("inv_sub")} <span className="text-mist font-semibold">{(req.amount / 100).toFixed(2)} €</span> · {req.email}
            </p>
            {done ? (
              <div className="rounded-2xl border border-gold/40 bg-gold/5 p-8 text-center" data-testid="invoice-done">
                <CheckCircle2 className="w-10 h-10 text-gold mx-auto mb-4" />
                <p className="text-gold text-sm leading-relaxed">{t("inv_done")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="invoice-form">
                <div>
                  <span className={label}>{t("inv_kind")}</span>
                  <div className="flex gap-3">
                    {[["fattura", t("inv_kind_invoice")], ["ricevuta", t("inv_kind_receipt")]].map(([v, l]) => (
                      <button type="button" key={v} onClick={() => setForm({ ...form, kind: v })}
                        data-testid={`invoice-kind-${v}`}
                        className={`flex-1 rounded-full border px-4 py-3 text-sm font-semibold transition-colors ${form.kind === v ? "border-ember bg-ember/10 text-ember" : "border-white/15 text-mute hover:border-white/30"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div><span className={label}>{t("inv_name")}</span>
                  <input required value={form.name} onChange={set("name")} className={field} data-testid="invoice-name" /></div>
                <div><span className={label}>{t("inv_street")}</span>
                  <input required value={form.address_street} onChange={set("address_street")} className={field} data-testid="invoice-street" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><span className={label}>{t("inv_cap")}</span>
                    <input required value={form.address_postal_code} onChange={set("address_postal_code")} className={field} data-testid="invoice-cap" /></div>
                  <div><span className={label}>{t("inv_province")}</span>
                    <input value={form.address_province} onChange={set("address_province")} maxLength={2} className={field} data-testid="invoice-province" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><span className={label}>{t("inv_city")}</span>
                    <input required value={form.address_city} onChange={set("address_city")} className={field} data-testid="invoice-city" /></div>
                  <div><span className={label}>{t("inv_country")}</span>
                    <input value={form.country} onChange={set("country")} className={field} data-testid="invoice-country" /></div>
                </div>
                {form.kind === "fattura" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div><span className={label}>{t("inv_vat")}</span>
                        <input value={form.vat_number} onChange={set("vat_number")} className={field} data-testid="invoice-vat" /></div>
                      <div><span className={label}>{t("inv_cf")}</span>
                        <input value={form.tax_code} onChange={set("tax_code")} className={field} data-testid="invoice-cf" /></div>
                    </div>
                    <p className="text-[11px] text-mute -mt-2">{t("inv_vat_hint")}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div><span className={label}>{t("inv_sdi")}</span>
                        <input value={form.sdi_code} onChange={set("sdi_code")} maxLength={7} className={field} data-testid="invoice-sdi" /></div>
                      <div><span className={label}>{t("inv_pec")}</span>
                        <input type="email" value={form.pec} onChange={set("pec")} className={field} data-testid="invoice-pec" /></div>
                    </div>
                  </>
                )}
                {error && <p className="text-sm text-red-400" data-testid="invoice-error">{error}</p>}
                <button disabled={busy} data-testid="invoice-submit"
                  className="w-full rounded-full bg-ember py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
                  {busy ? t("inv_sending") : t("inv_submit")}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default InvoiceRequest;
